/**
 * Tools → Integrations: Mercury, the half that asks customers for money.
 *
 * Two jobs, in the order somebody needs them:
 *
 * 1. **Say where the money lands.** This is the setting that matters and the
 *    one it is worth being slow about — the account chosen here is where every
 *    customer deposit is deposited. It is a dropdown of the company's own
 *    accounts read live from Mercury, not a field to paste an id into, because
 *    a mistyped account id is a deposit that goes somewhere nobody notices.
 * 2. **Send what is waiting, and say what happened.** Signing sends the request
 *    by itself; this button is for anything that did not go first time. What
 *    was refused is counted there in words rather than listed: Jonathon asked
 *    for the table to go on 2026-09-07, and a screen he does not read is not a
 *    safety net.
 *
 * The API token is never here. It lives in an Edge Function secret and the
 * `mercury` function is the only thing that ever holds it (CLAUDE.md rule 7).
 *
 * Admin only, unlike Gmail next door: where the company's money is deposited
 * is not a rep's decision.
 */

import { cents, formatMoney } from "@roof-hawk/core";
import { useCallback, useEffect, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { fmtDateOnly } from "../../lib/format";
import { fetchDepositRules, forgetDepositRule } from "../../lib/payments";
import { fetchMercuryQueue, type MercuryPaymentRow } from "../../lib/queries";
import { supabase } from "../../lib/supabase";
import { useSession } from "../../session";

interface MercurySettings {
  destination_account_id: string | null;
  destination_account_name: string | null;
  card_limit_cents: number | string;
  enabled: boolean;
}

interface Account {
  id: string;
  name: string;
}

/** Translated where shown; each label is in the dictionary. */
const SLOT_LABEL: Record<number, string> = {
  1: "First",
  2: "Second",
  3: "Third",
};

/** What the customer picked, in the words the screen uses (translated where shown). */
const CHOICE_LABEL: Record<string, string> = {
  card: "Credit card",
  ach: "Bank transfer",
  // An application is not a payment: the money is still outstanding, and the
  // row stays open. Worth saying plainly on the screen rather than leaving
  // somebody to read "financing" as "settled".
  financing: "Financing (not paid)",
};

function slotLabel(slot: number): string {
  const label = SLOT_LABEL[slot];
  return label ? t(label) : String(slot);
}

function choiceLabel(method: string | null | undefined): string {
  const label = CHOICE_LABEL[method ?? ""];
  return label ? t(label) : "—";
}

/**
 * The parts of `company_settings` this screen owns.
 *
 * Named once, and every save spreads what was there rather than listing the
 * others back — listing them meant a new setting had to be added to four
 * unrelated callbacks, and the one that was forgotten would blank a column
 * somebody else had just set.
 */
interface CompanyBits {
  financing_application_url: string | null;
  financing_offered: boolean | null;
  money_notify_profile_id: string | null;
  /** The programme every estimate starts from — see `saveFinancingTerms`. */
  financing_term_months: number | null;
  financing_apr: number | string | null;
  /** The calculator's programmes. Not edited here — see ADR 0202. */
  financing_programs: unknown;
}

const blankCompany: CompanyBits = {
  financing_application_url: null,
  financing_offered: null,
  money_notify_profile_id: null,
  financing_term_months: null,
  financing_apr: null,
  financing_programs: null,
};

export function MercuryPayments() {
  const { isAdmin, people } = useSession();

  const [settings, setSettings] = useState<MercurySettings | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [queue, setQueue] = useState<MercuryPaymentRow[]>([]);
  const [company, setCompany] = useState<CompanyBits | null>(null);
  const [rules, setRules] = useState<
    {
      id: string;
      label: string;
      counterparty: string;
      timesSetAside: number;
      reason: string;
    }[]
  >([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const invoke = useCallback(
    async (body: Record<string, unknown>): Promise<Record<string, unknown>> => {
      const { data, error: fnError } = await supabase.functions.invoke(
        "mercury",
        { body },
      );
      if (fnError) throw new Error(await whatMercurySaid(fnError));
      const result = (data ?? {}) as Record<string, unknown>;
      if (typeof result.error === "string") throw new Error(result.error);
      return result;
    },
    [],
  );

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const result = await invoke({ action: "status" });
      setSettings((result.settings as MercurySettings | null) ?? null);
      setQueue(await fetchMercuryQueue());
      const { data: row } = await supabase
        .from("company_settings")
        // `*` rather than three names. This is the only screen that can turn
        // financing back on, and naming a column production does not have yet
        // would 400 the whole select and leave the switch unreachable from the
        // one place it lives.
        .select("*")
        .eq("id", 1)
        .maybeSingle();
      setCompany((row as CompanyBits | null) ?? null);
      setRules(await fetchDepositRules());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [invoke, isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(
    async (work: () => Promise<string | null>) => {
      setBusy(true);
      setError(null);
      setMessage(null);
      try {
        setMessage(await work());
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  const loadAccounts = useCallback(async () => {
    const result = await invoke({ action: "accounts" });
    const list = (result.accounts as Account[] | undefined) ?? [];
    setAccounts(list);
    return list.length
      ? null
      : t("Mercury returned no accounts a deposit could land in.");
  }, [invoke]);

  /**
   * The lender link, which lives on `company_settings` rather than
   * `mercury_settings` — it has nothing to do with the bank, and every other
   * customer-facing company fact is already there (rule 11: the name is data).
   */
  const saveFinancingUrl = useCallback(async (url: string) => {
    const { error: saveError } = await supabase
      .from("company_settings")
      .update({ financing_application_url: url || null })
      .eq("id", 1);
    if (saveError) throw new Error(saveError.message);
    setCompany((was) => ({
      ...blankCompany,
      ...was,
      financing_application_url: url || null,
    }));
    return url
      ? t("Saved.")
      : t("There is no lender link, so no card is shown.");
  }, []);

  /**
   * Whether the company may offer financing at all.
   *
   * Jonathon, 2026-09-08: "let's hide financing for now on all jobs until we
   * get final approval from foundation finance." A permission rather than a
   * preference, so it overrules every per-estimate setting — which is why it
   * is a switch of its own and not the existing default.
   */
  const saveFinancingOffered = useCallback(async (offered: boolean) => {
    const { error: saveError } = await supabase
      .from("company_settings")
      .update({ financing_offered: offered })
      .eq("id", 1);
    if (saveError) throw new Error(saveError.message);
    setCompany((was) => ({
      ...blankCompany,
      ...was,
      financing_offered: offered,
    }));
    return offered
      ? t(
          "Financing is being offered again. Every estimate goes back to its own setting.",
        )
      : t(
          "Financing is hidden on every job — the page, the monthly figures and the apply card.",
        );
  }, []);

  /**
   * The term and the rate every estimate starts from.
   *
   * Foundation Finance publishes its programmes and changes them; a rate in
   * the source would make each change a deploy, so it is a row here and an
   * estimate may still set its own (ADR 0033, ADR 0201). Clearing either one
   * takes the payment off every proposal that was relying on it and puts the
   * old "$13 per $1,000" illustration back, which is why the message says so.
   */
  const saveFinancingTerms = useCallback(
    async (patch: {
      financing_term_months?: number | null;
      financing_apr?: number | null;
    }) => {
      const { error: saveError } = await supabase
        .from("company_settings")
        .update(patch)
        .eq("id", 1);
      if (saveError) throw new Error(saveError.message);
      setCompany((was) => ({ ...blankCompany, ...was, ...patch }));
      return Object.values(patch).some((value) => value == null)
        ? t(
            "Cleared. Estimates without their own terms show the old “about $13 per $1,000” figure instead of a payment.",
          )
        : t("Saved.");
    },
    [],
  );

  /** Who hears when money lands. A row, not a name in the source. */
  const saveMoneyNotify = useCallback(async (profileId: string) => {
    const { error: saveError } = await supabase
      .from("company_settings")
      .update({ money_notify_profile_id: profileId || null })
      .eq("id", 1);
    if (saveError) throw new Error(saveError.message);
    setCompany((was) => ({
      ...blankCompany,
      ...was,
      money_notify_profile_id: profileId || null,
    }));
    return profileId ? t("Saved.") : t("Nobody will be notified.");
  }, []);

  const save = useCallback(
    async (patch: Record<string, unknown>) => {
      const result = await invoke({ action: "settings", ...patch });
      setSettings((result.settings as MercurySettings | null) ?? null);
      return t("Saved.");
    },
    [invoke],
  );

  if (!isAdmin) return null;

  const waiting = queue.filter(
    (row) => row.state === "pending" && !row.last_error,
  );
  const outstanding = queue.filter((row) => row.state === "sent");

  const ready = Boolean(settings?.destination_account_id);

  return (
    <>
      <div className="block">
        <h3>{t("Mercury — customer payments")}</h3>
        <ErrorNote error={error} />
        {message && <div className="muted">{message}</div>}

        {settings?.enabled && ready ? (
          <div>
            {t("On. Deposits land in")}{" "}
            <b>
              {settings.destination_account_name ?? t("the chosen account")}
            </b>
            .
          </div>
        ) : (
          <div>
            {t(
              "Off. No customer is being asked for a deposit. Jobs marked Test still are, so a payment can be tried end to end.",
            )}
            {!ready && " " + t("Choose an account below first.")}
          </div>
        )}

        <div className="muted">
          {t(
            "A customer is asked for their first payment the moment they sign — half the contract on jobs of $1,000 and over, nothing at all below that, where the whole amount is due on completion. The second and third payments are not sent from here; they are released when the work they are tied to is finished.",
          )}
        </div>

        <div className="row">
          <button disabled={busy} onClick={() => void run(loadAccounts)}>
            {accounts.length
              ? t("Reload accounts")
              : t("Load my Mercury accounts")}
          </button>

          {accounts.length > 0 && (
            <select
              value={settings?.destination_account_id ?? ""}
              disabled={busy}
              onChange={(e) => {
                const chosen = accounts.find((a) => a.id === e.target.value);
                void run(() =>
                  save({
                    destinationAccountId: chosen?.id ?? "",
                    destinationAccountName: chosen?.name ?? "",
                  }),
                );
              }}
            >
              <option value="">{t("Where should deposits land?")}</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="row">
          <label>
            <input
              type="checkbox"
              disabled={busy || !ready}
              checked={Boolean(settings?.enabled)}
              onChange={(e) =>
                void run(() => save({ enabled: e.target.checked }))
              }
            />{" "}
            {t("Ask customers for a deposit when they sign")}
          </label>
        </div>

        {/* The card ceiling was here until 2026-09-06 — "Offer a card on jobs
            up to N dollars", `mercury_settings.card_limit_cents`. It existed
            because the company paid Stripe's 2.9% + 30c; the customer now pays
            3% themselves when they choose a card, so there is nothing left for
            it to protect. The column stays in the database, unread, because an
            applied migration is never edited. */}
        <div className="muted">
          {t(
            "A customer who signs is shown three ways to pay: apply for financing, pay by credit card with a 3% convenience fee added, or pay by bank transfer with no fee. Mercury emails nobody — the request comes from the rep’s own mailbox, and a reminder follows every other day until the money arrives or somebody cancels it below.",
          )}
        </div>

        <div className="row">
          <label>
            <input
              type="checkbox"
              disabled={busy}
              checked={company?.financing_offered === true}
              onChange={(e) =>
                void run(() => saveFinancingOffered(e.target.checked))
              }
            />{" "}
            {t("Offer financing to customers")}
          </label>
        </div>
        <div className="muted">
          {t(
            "Off since 2026-09-08, waiting on final approval from Foundation Finance. While it is off, no job shows a financing page, no price carries a “$130/month” beside it, and the payment page offers two ways to pay instead of three — whatever an individual estimate says. Turning it back on returns every estimate to its own setting; nothing was overwritten.",
          )}
        </div>

        <div className="row">
          <label>
            {t("Financing application link")}{" "}
            <input
              type="url"
              placeholder="https://"
              size={40}
              disabled={busy}
              defaultValue={company?.financing_application_url ?? ""}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next === (company?.financing_application_url ?? "")) return;
                void run(() => saveFinancingUrl(next));
              }}
            />
          </label>
        </div>
        <div className="muted">
          {t(
            "Where “Apply for financing” sends the customer. Leave it empty and that card is not shown at all, rather than being a button that goes nowhere.",
          )}
        </div>

        <div className="row">
          <label>
            {t("Default term (months)")}{" "}
            <input
              type="number"
              min={1}
              max={240}
              step={1}
              size={6}
              disabled={busy}
              defaultValue={company?.financing_term_months ?? ""}
              onBlur={(e) => {
                const raw = e.target.value.trim();
                const next = raw === "" ? null : Number(raw);
                if (
                  next != null &&
                  (!Number.isInteger(next) || next < 1 || next > 240)
                ) {
                  setError(t("Enter a whole number of months from 1 to 240."));
                  return;
                }
                if (next === (company?.financing_term_months ?? null)) return;
                void run(() =>
                  saveFinancingTerms({ financing_term_months: next }),
                );
              }}
            />
          </label>
          <label>
            {t("Default rate (APR %)")}{" "}
            <input
              type="number"
              min={0}
              max={99.99}
              step={0.01}
              size={6}
              disabled={busy}
              defaultValue={company?.financing_apr ?? ""}
              onBlur={(e) => {
                const raw = e.target.value.trim();
                const next = raw === "" ? null : Number(raw);
                if (
                  next != null &&
                  (!Number.isFinite(next) ||
                    next < 0 ||
                    next > 99.99 ||
                    Math.abs(Math.round(next * 100) - next * 100) > 1e-8)
                ) {
                  setError(
                    t(
                      "Enter an APR from 0 to 99.99 with at most two decimal places.",
                    ),
                  );
                  return;
                }
                const was =
                  company?.financing_apr == null
                    ? null
                    : Number(company.financing_apr);
                if (next === was) return;
                void run(() => saveFinancingTerms({ financing_apr: next }));
              }}
            />
          </label>
        </div>
        <div className="muted">
          {t(
            "The programme every estimate is quoted on, unless a rep types something else on the job itself. At 11.9% over 120 months a $10,000 loan is $142.90 a month, and that is the figure the customer’s proposal prints beside the price. Leave either one empty and proposals go back to showing “about $13 per $1,000” instead of a payment. It is an estimate either way — the lender sets the real rate when they approve somebody.",
          )}
        </div>

        <div className="row">
          <label>
            {t("Tell this person when money lands")}{" "}
            <select
              disabled={busy}
              value={company?.money_notify_profile_id ?? ""}
              onChange={(e) => void run(() => saveMoneyNotify(e.target.value))}
            >
              <option value="">{t("Nobody")}</option>
              {people
                .filter((person) => person.active !== false)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.full_name}
                  </option>
                ))}
            </select>
          </label>
        </div>
        <div className="muted">
          {t(
            "They get one notification per deposit that needs a person. Money a learned rule sets aside, and money QuickBooks has already matched to a customer, are not announced — there is nothing for anybody to do.",
          )}
        </div>

        {rules.length > 0 && (
          <>
            <div className="row" style={{ marginTop: 12 }}>
              <b>{t("Payers the Money In list has learned")}</b>
            </div>
            <table className="grid">
              <thead>
                <tr>
                  <th>{t("Payer")}</th>
                  <th>{t("Why")}</th>
                  <th>{t("Set aside")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>{rule.label || rule.counterparty}</td>
                    <td>{rule.reason}</td>
                    <td>
                      {rule.timesSetAside}
                      {rule.timesSetAside < 2 ? " · " + t("still asking") : ""}
                    </td>
                    <td>
                      <button
                        disabled={busy}
                        onClick={() =>
                          void run(async () => {
                            await forgetDepositRule(rule.id);
                            return t(
                              "Roof Board will ask about {name} again.",
                              {
                                name: rule.label || rule.counterparty,
                              },
                            );
                          })
                        }
                      >
                        {t("Forget")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="muted">
              {t(
                "Learned from what people set aside, never from what they matched. A deposit for an amount some job is waiting for always goes to a person, however familiar the payer.",
              )}
            </div>
          </>
        )}

        <div className="row">
          <button disabled={busy} onClick={() => void run(sendWaiting)}>
            {waiting.length > 0
              ? t("Send {n} waiting", { n: waiting.length })
              : t("Send now")}
          </button>
          <button disabled={busy} onClick={() => void run(checkPaid)}>
            {t("Check for payments")}
          </button>
        </div>
      </div>

      {outstanding.length > 0 && (
        <div className="block">
          <h3>{t("Waiting on the customer")}</h3>
          <table className="grid">
            <thead>
              <tr>
                <th>{t("Job")}</th>
                <th>{t("Payment")}</th>
                <th>{t("Amount")}</th>
                <th>{t("Chose")}</th>
                <th>{t("Sent")}</th>
                <th>{t("Reminders")}</th>
                <th>{t("Link")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {outstanding.map((row) => (
                <tr key={row.id}>
                  <td>{row.job?.job_number ?? "—"}</td>
                  {/* A manual link holds no slot at all — what it is for is
                      its own sentence (ADR 0141). */}
                  <td>
                    {row.slot == null
                      ? (row.purpose ?? t("Payment link"))
                      : slotLabel(row.slot)}
                  </td>
                  <td>
                    {row.amount_cents == null
                      ? "—"
                      : formatMoney(cents(Number(row.amount_cents)))}
                  </td>
                  <td>{choiceLabel(row.customer_method)}</td>
                  <td>{row.sent_at ? fmtDateOnly(row.sent_at) : "—"}</td>
                  <td>
                    {row.payment_started_at ? (
                      // They have paid; the bank is clearing it. No more
                      // letters, and the row goes when Mercury says Paid.
                      <span
                        title={t("Payment started {date}", {
                          date: fmtDateOnly(row.payment_started_at),
                        })}
                      >
                        {t("Paying · stopped")}
                      </span>
                    ) : (
                      <>
                        {row.reminder_count ?? 0}
                        {row.last_letter_at
                          ? ` · ${fmtDateOnly(row.last_letter_at)}`
                          : ""}
                      </>
                    )}
                  </td>
                  <td>
                    {row.payment_url ? (
                      <CopyLink url={row.payment_url} />
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {/* The other thing that stops the reminders. The money
                        arriving is the first. */}
                    <button
                      disabled={busy}
                      onClick={() => void run(() => cancelRequest(row))}
                    >
                      {t("Stop asking")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  async function cancelRequest(row: MercuryPaymentRow): Promise<string> {
    const reason =
      window
        .prompt(
          t(
            "Stop asking this customer for the payment. Why? (Everybody who looks at this job will read it.)",
          ),
        )
        ?.trim() ?? "";
    // Cancelled with no reason at all is a row nobody can explain in a month.
    if (!reason) return t("Left as it was.");
    await invoke({ action: "cancel", requestId: row.id, reason });
    return t("Stopped. No more reminders will go out for that payment.");
  }

  async function sendWaiting(): Promise<string> {
    const result = await invoke({ action: "send" });
    if (typeof result.message === "string") return result.message;
    const sent = Number(result.sent ?? 0);
    const skipped = Number(result.skipped ?? 0);
    const waiting = Number(result.waiting ?? 0);
    if (sent === 0 && skipped === 0 && waiting === 0) {
      return t("Nothing was waiting.");
    }
    const parts = [t("Sent {n}.", { n: sent })];
    if (waiting) {
      parts.push(
        t("{n} waiting for the customer to choose how to pay.", {
          n: waiting,
        }),
      );
    }
    if (skipped) parts.push(t("{n} could not go — see below.", { n: skipped }));
    return parts.join(" ");
  }

  async function checkPaid(): Promise<string> {
    const result = await invoke({ action: "sync" });
    const paid = Number(result.paid ?? 0);
    return paid ? t("{n} paid.", { n: paid }) : t("Nothing new has been paid.");
  }
}

/**
 * What the function actually said, rather than that it said something.
 *
 * `supabase.functions.invoke` turns every non-2xx into the one sentence "Edge
 * Function returned a non-2xx status code", leaves `data` null and puts the
 * real response on `context`. The mercury function spends most of api.ts
 * deciding which sentence is worth showing — "Mercury rejected the API token",
 * "the token cannot reach invoices", "Mercury is not configured" — and all of
 * that was landing in the bin one line before the screen. This is the same
 * shape as invokeGmail() in lib/gmail.ts, for the same reason.
 */
async function whatMercurySaid(error: {
  message: string;
  context?: unknown;
}): Promise<string> {
  const context = error.context;
  if (context instanceof Response) {
    const payload = (await context.json().catch(() => null)) as {
      error?: unknown;
    } | null;
    if (typeof payload?.error === "string" && payload.error.trim()) {
      return payload.error;
    }
    // No body to read. A 404 is the one status that means the function itself
    // is missing rather than Mercury refusing, and it is worth naming: the
    // rest of the screen looks identical either way.
    if (context.status === 404) {
      return t(
        "The mercury Edge Function is not deployed to this project. Deploy it (supabase functions deploy mercury) and set MERCURY_API_TOKEN.",
      );
    }
  }
  return error.message;
}

/**
 * The payment link, to copy and text to a customer.
 *
 * Jonathon asked for this by name: email now, "ideal if there's a link we can
 * copy and text to the customer". Shown as a button rather than a bare URL
 * because these links are long and selecting one by hand on a phone is how
 * half a link gets sent.
 */
function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard.writeText(url).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      {copied ? t("Copied") : t("Copy link")}
    </button>
  );
}
